import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";

/**
 * Scroll-scrubbed cinematic intro for nuvola.
 * - The page is 600vh tall.
 * - Within a sticky viewport we play the video by mapping scroll progress -> video.currentTime.
 * - Near the end of scroll, a soft cloud bloom fills the screen and an email form fades in.
 * - On submit the parent transitions to the game stage.
 */
export default function NuvolaLanding({ onSubmitEmail, stage, audioOn, toggleAudio }) {
  const wrapperRef = useRef(null);
  const videoRef = useRef(null);
  const stickyRef = useRef(null);
  const cloudRef = useRef(null);
  const formRef = useRef(null);
  const headlineRef = useRef(null);
  const videoStageRef = useRef(null);
  const fogRef = useRef(null);
  const targetTimeRef = useRef(0);
  const rafRef = useRef(0);
  const parallaxRef = useRef({ x: 0, y: 0, tx: 0, ty: 0 });
  const progressRef = useRef(0);
  const startRef = useRef(0);
  const [duration, setDuration] = useState(18);
  const [progress, setProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [isDesktop] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(min-width: 640px)").matches
  );
  const videoSrc = isDesktop ? "/assets/scene_desktop.mp4" : "/assets/scene.mp4";
  const posterSrc = isDesktop ? "/assets/scene_desktop_poster.jpg" : "/assets/scene_poster.jpg";
  const [email, setEmail] = useState("");

  const onLoadedMeta = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    setDuration(v.duration || 18);
    // Force the first frame to actually render. Some browsers (especially
    // mobile Safari + Chrome on Android) won't paint a paused video until
    // it has been played at least once, even when currentTime is set.
    const showFirstFrame = async () => {
      try {
        await v.play();
        requestAnimationFrame(() => {
          try {
            v.pause();
            v.currentTime = targetTimeRef.current || 0;
          } catch (_) { /* noop */ }
          setVideoReady(true);
        });
      } catch (_) {
        // play() rejected (autoplay blocked) — still mark ready.
        try { v.currentTime = 0; } catch (_) { /* noop */ }
        setVideoReady(true);
      }
    };
    showFirstFrame();
  }, []);

  // Mobile iOS Safari blocks currentTime seeks on a video that hasn't been
  // played by user gesture. Attach a one-time touch/click listener that
  // performs a silent play+pause so subsequent scroll-scrubs work.
  useEffect(() => {
    let done = false;
    const unlock = async () => {
      if (done) return;
      done = true;
      const v = videoRef.current;
      if (!v) return;
      try {
        await v.play();
        requestAnimationFrame(() => {
          try { v.pause(); } catch (_) { /* noop */ }
          try { v.currentTime = targetTimeRef.current || 0; } catch (_) { /* noop */ }
        });
      } catch (_) { /* noop */ }
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("touchend", unlock);
      window.removeEventListener("click", unlock);
      window.removeEventListener("scroll", unlock);
    };
    window.addEventListener("touchstart", unlock, { passive: true, once: false });
    window.addEventListener("touchend", unlock, { passive: true, once: false });
    window.addEventListener("click", unlock, { once: false });
    window.addEventListener("scroll", unlock, { passive: true, once: false });
    return () => {
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("touchend", unlock);
      window.removeEventListener("click", unlock);
      window.removeEventListener("scroll", unlock);
    };
  }, []);

  // Scroll listener: compute progress 0..1 over the wrapper's scroll range
  useEffect(() => {
    const computeProgress = () => {
      const el = wrapperRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = el.offsetHeight - window.innerHeight;
      const p = Math.min(1, Math.max(0, -rect.top / Math.max(1, total)));
      progressRef.current = p;
      setProgress(p);
      const videoP = Math.min(1, p / 0.88);
      targetTimeRef.current = videoP * (duration || 18);
    };
    computeProgress();
    window.addEventListener("scroll", computeProgress, { passive: true });
    window.addEventListener("resize", computeProgress);
    return () => {
      window.removeEventListener("scroll", computeProgress);
      window.removeEventListener("resize", computeProgress);
    };
  }, [duration]);

  // Smooth currentTime lerp + DOM-driven shake/parallax via rAF
  useEffect(() => {
    const tick = () => {
      if (!startRef.current) startRef.current = performance.now();
      const v = videoRef.current;
      if (v && videoReady) {
        // Keep the video paused so scroll fully drives playback
        if (!v.paused) { try { v.pause(); } catch (_) { /* noop */ } }
        const cur = v.currentTime;
        const target = targetTimeRef.current;
        const next = cur + (target - cur) * 0.18;
        if (Math.abs(next - cur) > 0.005) {
          try { v.currentTime = next; } catch (_) { /* seek may throw before metadata */ }
        }
      }
      // parallax smoothing
      const p = parallaxRef.current;
      p.x += (p.tx - p.x) * 0.06;
      p.y += (p.ty - p.y) * 0.06;
      if (headlineRef.current) {
        headlineRef.current.style.transform = `translate3d(${p.x * -10}px, ${p.y * -6}px, 0)`;
      }
      // shake + parallax on video stage
      const t = (typeof performance !== "undefined" ? performance.now() : 0) - startRef.current;
      const prog = progressRef.current;
      const videoP = Math.min(1, prog / 0.88);
      const subShake = videoP > 0.4 ? (videoP - 0.4) * 18 : 0;
      const shakeX = subShake * Math.sin(t * 0.012) * 0.5;
      const shakeY = subShake * Math.cos(t * 0.014) * 0.5;
      if (videoStageRef.current) {
        videoStageRef.current.style.transform =
          `translate3d(${shakeX}px, ${shakeY}px, 0) scale(${1 + videoP * 0.02})`;
      }
      if (fogRef.current) {
        fogRef.current.style.transform =
          `translate3d(${p.x * 30}px, ${p.y * 20}px, 0)`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [videoReady]);

  // Mouse / touch parallax
  useEffect(() => {
    const onMove = (e) => {
      const x = (e.touches ? e.touches[0].clientX : e.clientX) / window.innerWidth - 0.5;
      const y = (e.touches ? e.touches[0].clientY : e.clientY) / window.innerHeight - 0.5;
      parallaxRef.current.tx = x;
      parallaxRef.current.ty = y;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
    };
  }, []);

  // Show form when progress > 0.88 (derived from scroll)
  const showForm = progress > 0.88;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || submitting) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast("invalid email — try again");
      return;
    }
    setSubmitting(true);
    // Cloud pulse before parent transitions
    await new Promise((r) => setTimeout(r, 600));
    onSubmitEmail(email);
  };

  // Derived values
  const cloudOpacity = Math.max(0, Math.min(1, (progress - 0.78) / 0.18));
  const cloudScale = 0.6 + cloudOpacity * 1.6;
  const headlineOpacity = Math.max(0, 1 - progress / 0.45);
  const uiOpacity = Math.max(0, 1 - cloudOpacity * 1.2);

  const isExiting = stage === "transitioning";

  return (
    <div ref={wrapperRef} className="relative w-full" style={{ height: "160vh" }} data-testid="landing-wrapper">
      {/* Sticky viewport */}
      <div
        ref={stickyRef}
        className="sticky top-0 w-full h-screen overflow-hidden bg-black"
        data-testid="landing-sticky"
      >
        {/* Video layer */}
        <div
          ref={videoStageRef}
          className="absolute inset-0"
        >
          <video
            ref={videoRef}
            src={videoSrc}
            poster={posterSrc}
            preload="auto"
            playsInline
            muted
            autoPlay
            onLoadedMetadata={onLoadedMeta}
            className="w-full h-full object-contain sm:object-cover bg-black"
            style={{ objectPosition: isDesktop ? "50% 50%" : "55% 50%" }}
            data-testid="scene-video"
          />
          {/* fog overlays for added depth */}
          <div
            ref={fogRef}
            className="absolute inset-0 mix-blend-screen pointer-events-none"
            style={{
              background:
                "radial-gradient(60% 50% at 30% 30%, rgba(220,220,220,0.10), transparent 60%), radial-gradient(50% 40% at 80% 70%, rgba(180,180,180,0.10), transparent 60%)",
            }}
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 22%, rgba(0,0,0,0) 78%, rgba(0,0,0,0.7) 100%)",
            }}
          />
        </div>

        {/* Top brand bar — minimal: logo + audio toggle only, fade out as cloud takes over */}
        <div
          className="absolute top-0 inset-x-0 z-30 flex items-center justify-between px-6 sm:px-10 py-5 text-white/80"
          style={{ opacity: uiOpacity, transition: "opacity 200ms linear" }}
        >
          <div className="flex items-center" data-testid="brand-mark">
            <img src="/assets/nuvola_w.png" alt="nuvola" className="h-5 sm:h-6 w-auto opacity-90" />
          </div>
          <button
            onClick={toggleAudio}
            className="opacity-60 hover:opacity-100 transition-opacity p-2 -m-2"
            data-testid="audio-toggle"
            aria-label="toggle ambient audio"
          >
            {audioOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </button>
        </div>

        {/* Center: intentionally empty during landing — no text until the cloud scene */}

        {/* Scroll affordance: tiny vertical line only, no text */}
        <div
          id="scroll-hint"
          className="absolute bottom-10 inset-x-0 z-20 flex flex-col items-center text-white/55 transition-opacity duration-700 pointer-events-none"
          style={{ opacity: progress > 0.04 ? 0 : 1 }}
          data-testid="scroll-hint"
        >
          <div className="w-px h-10 bg-white/40 mb-3 float-y" />
          <span className="font-mono text-[10px] tracking-[0.4em] uppercase">
            scroll to enter
          </span>
        </div>

        {/* Cloud bloom — fills from bottom, expanding into full viewport */}
        <div
          ref={cloudRef}
          className="absolute inset-0 z-30 pointer-events-none flex items-end justify-center"
          style={{ opacity: cloudOpacity }}
          data-testid="cloud-bloom"
        >
          <div
            className="rounded-full"
            style={{
              width: "140vmax",
              height: "140vmax",
              transform: `translateY(${(1 - cloudOpacity) * 50}vh) scale(${cloudScale})`,
              background:
                "radial-gradient(closest-side, rgba(245,245,245,0.98), rgba(230,230,230,0.85) 40%, rgba(200,200,200,0.55) 60%, rgba(120,120,120,0.0) 78%)",
              filter: "blur(8px)",
              transition: "transform 120ms linear",
            }}
          />
        </div>

        {/* Email form — appears inside cloud near end */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              ref={formRef}
              initial={{ opacity: 0, y: 24, filter: "blur(12px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -8, filter: "blur(16px)" }}
              transition={{ duration: 1.1, ease: [0.22, 0.61, 0.36, 1] }}
              className="absolute inset-0 z-40 flex flex-col items-center justify-center px-6"
              data-testid="email-form-container"
            >
              <div
                className={`text-center max-w-md w-full ${isExiting ? "cloud-pulse" : ""}`}
                style={{
                  // form lives inside the cloud → dark text on light cloud
                  color: "#111",
                }}
              >
                <p className="font-mono text-[9px] sm:text-[10px] tracking-[0.4em] sm:tracking-[0.5em] uppercase text-black/55 mb-6 sm:mb-8">
                  you have arrived
                </p>
                <img
                  src="/assets/nuvola_word.png"
                  alt="nuvola"
                  className="w-[78%] sm:w-[64%] max-w-md mx-auto mb-8 sm:mb-10 select-none"
                  draggable={false}
                  data-testid="nuvola-wordmark"
                />

                <form onSubmit={handleSubmit} className="w-full" data-testid="email-form">
                  <div className="border-b border-black/30 focus-within:border-black/80 transition-colors pb-2 mb-6">
                    <input
                      type="email"
                      autoFocus
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="enter email for early access"
                      className="w-full bg-transparent text-black placeholder-black/40 font-mono tracking-wider outline-none text-center"
                      style={{ fontSize: "16px" }}
                      data-testid="email-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="group inline-flex items-center justify-center gap-3 px-10 py-3 border border-black/70 text-black font-mono text-[11px] tracking-[0.45em] uppercase hover:bg-black hover:text-white transition-colors duration-500 disabled:opacity-50"
                    data-testid="email-submit"
                  >
                    <span>{submitting ? "falling…" : "fall"}</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="transition-transform group-hover:translate-y-0.5">
                      <path d="M12 5v14M6 13l6 6 6-6" />
                    </svg>
                  </button>
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bottom progress meter */}
        <div className="absolute bottom-0 left-0 right-0 z-20 h-px bg-white/10">
          <div
            className="h-full bg-white/70"
            style={{ width: `${progress * 100}%`, transition: "width 80ms linear" }}
            data-testid="scroll-progress"
          />
        </div>
      </div>
    </div>
  );
}
